<template>
  <div class="checkout-page">
    <MetaManager
      title="Checkout"
      description="Complete your order by providing billing details and payment information."
    />
    <h1>Checkout</h1>
    <div class="checkout-container">
      <div class="billing-details">
        <h2>Billing Details</h2>
        <form @input="saveBillingDetails">
          <div class="form-row">
            <div class="form-group">
              <label for="firstName">*First Name</label>
              <input type="text" id="firstName" v-model="billingDetails.firstName" required />
            </div>
            <div class="form-group">
              <label for="lastName">*Last Name</label>
              <input type="text" id="lastName" v-model="billingDetails.lastName" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="companyName">Company Name</label>
              <input type="text" id="companyName" v-model="billingDetails.companyName" />
            </div>
            <div class="form-group">
              <label for="address">*Address</label>
              <input type="text" id="address" v-model="billingDetails.address" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="city">*Town/City</label>
              <input type="text" id="city" v-model="billingDetails.city" required />
            </div>
            <div class="form-group">
              <label for="zipCode">*Zip Code</label>
              <input type="text" id="zipCode" v-model="billingDetails.zipCode" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="country">Country</label>
              <select id="country" v-model="billingDetails.country" required>
                <option v-for="country in countries" :key="country.code" :value="country.code">
                  {{ country.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="email">*Email Address</label>
              <input type="email" id="email" v-model="billingDetails.email" required />
            </div>
            <div class="form-group">
              <label for="phone">*Phone</label>
              <input type="tel" id="phone" v-model="billingDetails.phone" required />
            </div>
          </div>
        </form>
      </div>
      <div class="order-summary">
        <h2>Your Order</h2>
        <div v-for="(item, index) in checkoutCartItems" :key="index" class="order-item">
          <div class="item-details">
            <div class="item-name">{{ item.name }}</div>
            <div>QTY: {{ item.quantity }}</div>
            <button class="fine-print-link" @click="viewFinePrint(item)">View Gift Card Restrictions</button>
          </div>
          <div class="item-price">${{ (calculateDiscountedPrice(item.originalPrice, item.discount) * item.quantity).toFixed(2) }}</div>
        </div>
        
        <!-- Event Ticket Display -->
        <div v-if="discountType === 'ticket' && eventName" class="event-ticket-item">
          <div class="item-details">
            <div class="item-name">Event Ticket: {{ eventName }}</div>
            <div>Ticket Code: {{ appliedDiscountCode }}</div>
          </div>
        </div>
        
        <div class="subtotal-cost">
          <span>Subtotal</span>
          <span>${{ subtotalCost }}</span>
        </div>
        <div class="coupon-code">
          <label for="couponCode">Coupon Code</label>
          <input type="text" v-model="couponCode" placeholder="Enter coupon code" />
          <button @click="applyCoupon" :disabled="isProcessing">Apply Coupon</button>
          <div v-if="couponMessage">{{ couponMessage }}</div>
        </div>
          <!-- Shopper Bucks Section -->
        <div v-if="shopperBucks > 0 && parseFloat(subtotalCost) >= 20" class="shopper-bucks-section">
          <div class="shopper-bucks-header">
            <h3>Shopper Bucks</h3>
            <span class="available-amount">Available: ${{ shopperBucks.toFixed(2) }}</span>
          </div>
          
          <div class="shopper-bucks-controls">
            <div class="checkbox-container">
              <input 
                type="checkbox" 
                id="apply-shopper-bucks" 
                v-model="applyShopperBucks"
                @change="onShopperBucksToggle"
              />
              <label for="apply-shopper-bucks">
                Apply Shopper Bucks to your order
              </label>
            </div>
            
            <div v-if="applyShopperBucks && shopperBucksOptions.length > 0" class="amount-selection">
              <label for="shopper-bucks-amount">Select amount to apply:</label>
              <select 
                id="shopper-bucks-amount" 
                v-model="selectedShopperBucksAmount"
                class="amount-dropdown"
              >
                <option 
                  v-for="option in shopperBucksOptions" 
                  :key="option" 
                  :value="option"
                >
                  ${{ option.toFixed(2) }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Shopper Bucks Unavailable Message -->
        <div v-if="shopperBucks > 0 && parseFloat(subtotalCost) < 20" class="shopper-bucks-unavailable">
          <div class="shopper-bucks-header">
            <h3>Shopper Bucks</h3>
            <span class="available-amount">Available: ${{ shopperBucks.toFixed(2) }}</span>
          </div>
          <div class="unavailable-message">
            Shopper Bucks can only be applied to orders of $20.00 or more.
          </div>
        </div>
        
        <div class="discount-amount" v-if="discountAmount > 0">
          <span>Discount{{ appliedDiscountCode ? ` (${appliedDiscountCode})` : '' }}</span>
          <span>-${{ discountAmount }}</span>
        </div>
        <div class="shopper-bucks-discount" v-if="applyShopperBucks && selectedShopperBucksAmount > 0">
          <span>Shopper Bucks Applied</span>
          <span>-${{ selectedShopperBucksAmount.toFixed(2) }}</span>
        </div>
        <div class="total-cost">
          <span>Total Cost</span>
          <span>${{ totalCost }}</span>
        </div>
        <div class="terms-agreement">
          <input type="checkbox" id="terms-agreement-check" v-model="termsAgreed">
          <label for="terms-agreement-check">
            I have read the gift card restrictions of the gift card(s) I am purchasing and I agree to the 
            <a href="/terms-of-service" target="_blank">Terms of Service</a> and 
            <a href="/refund-policy" target="_blank">Refund Policy</a>.
            <strong>Click checkbox to continue checkout.</strong>
          </label>        </div>
        
        <!-- Free Order Section (when total is $0.00) -->
        <div v-if="termsAgreed && parseFloat(totalCost) === 0" class="free-order-section">
          <h2>Order Complete</h2>
          <div class="free-order-message">
            <div class="success-icon">✓</div>
            <p>Your order is fully covered by Shopper Bucks! No payment information is required.</p>
            <button 
              @click="handlePayment" 
              :disabled="isProcessing" 
              class="complete-order-button"
            >
              <div v-if="isProcessing" class="spinner"></div>
              <span v-if="isProcessing">Processing...</span>
              <span v-else>Complete Free Order</span>
            </button>
          </div>
        </div>
        
        <!-- Regular Payment Section (when total > $0.00) -->
        <div v-if="termsAgreed && parseFloat(totalCost) > 0" class="payment-section">
          <h2>Payment Details:</h2>
          <div class="payment-form">
            <div id="payment-element">
              <!-- Stripe Elements will be inserted here -->
            </div>
            <div id="payment-message" class="hidden"></div>
            <button 
              id="submit"
              @click="handlePayment" 
              :disabled="isProcessing || !stripe || !elements" 
              class="place-order-button"
            >
              <div v-if="isProcessing" class="spinner" id="spinner"></div>
              <span v-if="isProcessing">Processing...</span>
              <span v-else>Pay ${{ totalCost }}</span>
            </button>
          </div>
        </div>
        <div v-if="transactionMessage" class="transaction-message">{{ transactionMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MetaManager from '../../components/MetaManager.vue';
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'CheckoutPage',
  components: {
    MetaManager,
  },
  data() {
    return {
      messageTimeout: null,
      transactionMessage: '',
      isProcessing: false,
      paymentIntentProcessing: false,
      termsAgreed: false,
      billingDetails: {
        firstName: '',
        lastName: '',
        companyName: '',
        address: '',
        city: '',
        zipCode: '',
        country: 'US',
        email: '',
        phone: ''
      },
      taxRate: 0.065,
      checkoutCartItems: [],
      countries: [],
      agreedToTerms: false,
      stripe: null,
      elements: null,
      paymentElement: null,
      clientSecret: null,
      discountAmount: 0,
      couponCode: '',
      couponMessage: '',
      appliedDiscountCode: '',
      discountType: '',
      eventName: '',
      // Shopper Bucks properties - ensure they're properly initialized
      shopperBucks: 0,
      applyShopperBucks: false,
      selectedShopperBucksAmount: 0,
      shopperBucksOptions: [],
      shopperBucksLoading: false,
    };
  },
  computed: {
    subtotalCost() {
      if (!this.checkoutCartItems || this.checkoutCartItems.length === 0) {
        return '0.00';
      }
      
      return this.checkoutCartItems.reduce((total, item) => {
        const discountedPrice = this.calculateDiscountedPrice(item.originalPrice, item.discount);
        return total + discountedPrice * (item.quantity || 1);
      }, 0).toFixed(2);
    },
    totalCost() {
      try {
        const subtotal = parseFloat(this.subtotalCost) || 0;
        const discount = parseFloat(this.discountAmount) || 0;
        const shopperBucksDiscount = this.applyShopperBucks ? parseFloat(this.selectedShopperBucksAmount) || 0 : 0;
        return Math.max(0, subtotal - discount - shopperBucksDiscount).toFixed(2);
      } catch (error) {
        console.error('Error calculating total:', error);
        return '0.00';
      }
    },
    isFormValid() {
      return !!(
        this.billingDetails?.firstName &&
        this.billingDetails?.lastName &&
        this.billingDetails?.address &&
        this.billingDetails?.city &&
        this.billingDetails?.zipCode &&
        this.billingDetails?.email &&
        this.billingDetails?.phone
      );
    },  },
  watch: {
    termsAgreed(newValue) {
      if (newValue && this.stripe && !this.clientSecret) {
        // User just agreed to terms, create payment intent
        this.createPaymentIntent();
      }
    },
    subtotalCost(newValue) {
      // Check if subtotal dropped below $20 minimum for Shopper Bucks
      const subtotal = parseFloat(newValue) || 0;
      if (subtotal < 20 && this.applyShopperBucks) {
        // Reset Shopper Bucks application if subtotal is below minimum
        this.applyShopperBucks = false;
        this.selectedShopperBucksAmount = 0;
      }
      
      // Regenerate Shopper Bucks options when subtotal changes
      if (this.shopperBucks > 0) {
        this.generateShopperBucksOptions();
      }
    },
    // Watch for changes in total cost and update payment intent accordingly
    totalCost: {
      handler(newTotal, oldTotal) {
        // Only update if terms are agreed, we have a payment intent, and the total actually changed
        if (this.termsAgreed && this.clientSecret && this.clientSecret !== 'free_order' && newTotal !== oldTotal) {
          const newAmount = parseFloat(newTotal);
          const oldAmount = parseFloat(oldTotal || '0');
          
          // Only update if there's a meaningful difference (more than 1 cent)
          if (Math.abs(newAmount - oldAmount) > 0.01) {
            console.log(`Total cost changed from $${oldAmount} to $${newAmount}, updating payment intent`);
            this.updatePaymentIntent(newAmount);
          }
        }
      },
      immediate: false
    },
    // Watch for changes in Shopper Bucks application
    applyShopperBucks(newValue) {
      if (this.termsAgreed && this.clientSecret && this.clientSecret !== 'free_order') {
        // Delay the update slightly to allow selectedShopperBucksAmount to be set
        this.$nextTick(() => {
          const currentTotal = parseFloat(this.totalCost);
          console.log(`Shopper Bucks application changed to ${newValue}, total is now $${currentTotal}`);
          this.updatePaymentIntent(currentTotal);
        });
      }
    },
    // Watch for changes in selected Shopper Bucks amount
    selectedShopperBucksAmount(newAmount, oldAmount) {
      if (this.termsAgreed && this.clientSecret && this.clientSecret !== 'free_order' && this.applyShopperBucks) {
        if (Math.abs(newAmount - (oldAmount || 0)) > 0.01) {
          const currentTotal = parseFloat(this.totalCost);
          console.log(`Shopper Bucks amount changed from $${oldAmount || 0} to $${newAmount}, total is now $${currentTotal}`);
          this.updatePaymentIntent(currentTotal);
        }
      }
    }
  },
  async mounted() {
    // Initialize checkout cart items from localStorage
    try {
      const storedItems = localStorage.getItem('checkoutCartItems');
      this.checkoutCartItems = storedItems ? JSON.parse(storedItems) : [];
    } catch (error) {
      console.error('Error parsing checkout cart items:', error);
      this.checkoutCartItems = [];
    }

    // Check for pending discount code and auto-apply
    const pendingCode = localStorage.getItem('pendingDiscountCode');
    if (pendingCode) {
      this.couponCode = pendingCode;
      localStorage.removeItem('pendingDiscountCode');
      // Wait a bit for component to fully load, then apply
      await this.$nextTick();
      setTimeout(() => {
        this.applyCoupon();
      }, 300);
    }

    // Load Stripe.js
    this.loadStripe();
    this.triggerBeginCheckoutEvent();

    this.loadBillingDetails();
    this.fetchCountries();
    this.fetchShopperBucks();
    window.addEventListener('cart-updated', this.updateCartItems);
  },
  beforeUnmount() {
    window.removeEventListener('cart-updated', this.updateCartItems);
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  },
  methods: {
    async loadStripe() {
      try {
        if (!process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
          throw new Error('Stripe publishable key is not configured');
        }
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        if (!this.stripe) {
          throw new Error('Failed to initialize Stripe');
        }
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        this.transactionMessage = error.message || 'Failed to initialize payment system.';
      }
    },
    async createPaymentIntent() {
      try {
        const totalAmount = parseFloat(this.totalCost);
        
        // If total is $0 or less (covered by Shopper Bucks), skip payment intent creation
        if (totalAmount <= 0) {
          console.log('Order total is $0.00, skipping payment intent creation');
          this.clientSecret = 'free_order'; // Set a flag to indicate free order
          return;
        }
        
        // Prepare items data for validation
        const itemsForValidation = this.checkoutCartItems.map(item => ({
          giftCardId: item.giftCardId,
          quantity: parseInt(item.quantity) || 1,
          optionId: item.optionId,
          originalPrice: parseFloat(item.originalPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          businessId: item.businessId,
          name: item.name,
          dealTitle: item.dealTitle
        }));
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/payment/create-payment-intent`,
          {
            amount: totalAmount,
            currency: 'usd',
            items: itemsForValidation, // Include items for validation
            metadata: {
              customer_email: this.billingDetails.email || 'guest@example.com',
              order_items: JSON.stringify(this.checkoutCartItems.map(item => ({
                name: item.name || 'Unknown Item',
                quantity: item.quantity || 1,
                price: this.calculateDiscountedPrice(item.originalPrice, item.discount)
              })))
            }
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to create payment intent');
        }

        if (!response.data.clientSecret) {
          throw new Error('Failed to create payment intent');
        }
        
        this.clientSecret = response.data.clientSecret;
        this.mountStripeElements();
      } catch (error) {
        console.error('Error creating payment intent:', error);
        this.transactionMessage = error.response?.data?.message || error.message || 'Unable to initialize payment. Please try again.';
        this.clearTransactionMessage();
      }
    },
    mountStripeElements() {
      // If this is a free order, don't mount Stripe elements
      if (this.clientSecret === 'free_order') {
        console.log('Free order detected, skipping Stripe Elements mounting');
        return;
      }
      
      if (!this.stripe || !this.clientSecret) {
        console.error('Cannot mount Stripe Elements - missing stripe or clientSecret');
        return;
      }
      const container = document.getElementById('payment-element');
      if (!container) {
        console.error('Payment element container not found in DOM');
        return;
      }
      container.innerHTML = '';
      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#4C6B30',
            colorBackground: '#ffffff',
            colorText: '#30313d',
            colorDanger: '#df1b41',
            fontFamily: 'Arial, sans-serif',
            spacingUnit: '4px',
            borderRadius: '4px'
          }
        }
      });
      this.paymentElement = this.elements.create('payment');
      this.paymentElement.mount('#payment-element');
    },
    async updatePaymentIntent(newAmount) {
      try {
        if (!this.clientSecret || this.clientSecret === 'free_order') {
          console.log('No payment intent to update or free order');
          return;
        }
        
        // If the new amount is 0 or less, we need to handle this as a free order
        if (newAmount <= 0) {
          console.log('Amount is now $0 or less, handling as free order');
          this.clientSecret = 'free_order';
          // Clear the Stripe elements since no payment is needed
          const container = document.getElementById('payment-element');
          if (container) {
            container.innerHTML = '<p style="text-align: center; color: #4C6B30; font-weight: bold;">No payment required - order total covered by discounts!</p>';
          }
          return;
        }
        
        console.log(`Updating payment intent for new amount: $${newAmount}`);
        
        // Prepare items data for validation
        const itemsForValidation = this.checkoutCartItems.map(item => ({
          giftCardId: item.giftCardId,
          quantity: parseInt(item.quantity) || 1,
          optionId: item.optionId,
          originalPrice: parseFloat(item.originalPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          businessId: item.businessId,
          name: item.name,
          dealTitle: item.dealTitle
        }));
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/payment/update-payment-intent`,
          {
            clientSecret: this.clientSecret,
            amount: newAmount,
            items: itemsForValidation
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update payment intent');
        }

        // Update the client secret if it changed
        if (response.data.clientSecret && response.data.clientSecret !== this.clientSecret) {
          this.clientSecret = response.data.clientSecret;
          console.log('Payment intent updated successfully, remounting Stripe elements');
          
          // Remount Stripe elements with the updated payment intent
          this.mountStripeElements();
        }
        
        console.log(`Payment intent updated successfully for amount: $${newAmount}`);
      } catch (error) {
        console.error('Error updating payment intent:', error);
        this.transactionMessage = error.response?.data?.message || error.message || 'Error updating payment details. Please refresh and try again.';
        this.clearTransactionMessage();
      }
    },
    preventScroll(event) {
      event.preventDefault();
      event.stopPropagation();
    },
    clearTransactionMessage() {
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
      }
      this.messageTimeout = setTimeout(() => {
        const messageEl = document.querySelector('.transaction-message');
        if (messageEl) {
          messageEl.classList.add('fade-out');
        }
        setTimeout(() => {
          this.transactionMessage = '';
          if (messageEl) {
            messageEl.classList.remove('fade-out');
          }
        }, 500);
      }, 4500);
    },
    triggerBeginCheckoutEvent() {
      if (this.checkoutCartItems.length > 0) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'begin_checkout',
          currency: 'USD',
          value: parseFloat(this.totalCost),
          items: this.checkoutCartItems.map(item => ({
            item_id: item.giftCardId,
            item_name: item.name,
            price: parseFloat(item.originalPrice),
            quantity: parseInt(item.quantity)
          }))
        });
      }
    },
    async confirmOrder(orderId) {
      try {
        const userId = JSON.parse(localStorage.getItem('user'))?.id;
        if (!userId) {
          throw new Error('User not logged in');
        }
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/payment/confirm-order`,
          {
            orderId,
            userId,
            items: this.checkoutCartItems.map(item => ({
              giftCardId: item.giftCardId,
              quantity: parseInt(item.quantity) || 1,
              originalPrice: parseFloat(item.originalPrice) || 0,
              businessId: item.businessId,
              name: item.name,
              discount: parseFloat(item.discount) || 0,
              dealTitle: item.dealTitle,
              optionId: item.optionId
            })),
            totalCost: this.totalCost,
            email: this.billingDetails.email,
            billingDetails: this.billingDetails,
            // Add discount code information
            discountCode: this.appliedDiscountCode || null,
            discountAmount: this.discountAmount || 0,
            discountType: this.discountType || null,
            eventName: this.eventName || null
          }
        );
        if (response.data.order) {
          this.$router.push({
            name: 'OrderConfirmation',
            params: {
              userId: userId,
              orderId: response.data.order.orderId,
              orderData: JSON.stringify(response.data.order)
            }
          }).then(() => {
            this.checkoutCartItems.forEach((item) => {
              this.removeFromCheckoutCart(item.giftCardId, item.name);
            });
          });
        }
      } catch (error) {
        console.error('Error confirming order:', error);
        throw error;
      }
    },
    recalculateCosts() {
      const subtotal = parseFloat(this.subtotalCost) || 0;
      const discount = parseFloat(this.discountAmount) || 0;
      const total = Math.max(0, subtotal - discount);
      this.totalCost = Number.isFinite(total) ? total.toFixed(2) : '0.00';
    },
    updateCheckoutCart(items) {
      this.checkoutCartItems = items || [];
      localStorage.setItem('checkoutCartItems', JSON.stringify(this.checkoutCartItems));
    },
    removeFromCheckoutCart(giftCardId, name) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.id) {
        this.checkoutCartItems = this.checkoutCartItems.filter(
          (item) => item.giftCardId !== giftCardId || item.name !== name
        );
        this.updateCheckoutCart(this.checkoutCartItems);
        const event = new CustomEvent('update-checkout-cart', { detail: this.checkoutCartItems });
        window.dispatchEvent(event);
        return;
      }
      const token = localStorage.getItem('token');
      axios.put(
        `${process.env.VUE_APP_API_URL}/api/shoppingCart/remove-from-cart/${user.id}`,
        { giftCardId, name },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      )
      .then(() => {
        this.checkoutCartItems = this.checkoutCartItems.filter(
          (item) => item.giftCardId !== giftCardId || item.name !== name
        );
        this.updateCheckoutCart(this.checkoutCartItems);
        const event = new CustomEvent('update-checkout-cart', { detail: this.checkoutCartItems });
        window.dispatchEvent(event);
      })
      .catch((error) => {
        console.error('Error removing from checkout cart:', error);
      });
    },
    async fetchCountries() {
      try {
        const response = await axios.get('https://restcountries.com/v2/all');
        this.countries = response.data.map(country => ({
          code: country.alpha2Code,
          name: country.name
        }));
        this.billingDetails.country = 'US';
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    },
    async fetchShopperBucks() {
      try {
        this.shopperBucksLoading = true;
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No auth token found, skipping Shopper Bucks fetch');
          return;
        }

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/referral`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        this.shopperBucks = parseFloat(response.data.shopper_bucks) || 0;
        this.generateShopperBucksOptions();
      } catch (error) {
        console.error('Error fetching Shopper Bucks:', error);
        this.shopperBucks = 0;
        this.shopperBucksOptions = [];
      } finally {
        this.shopperBucksLoading = false;
      }
    },
    generateShopperBucksOptions() {
      this.shopperBucksOptions = [];
      
      if (this.shopperBucks <= 0) {
        this.selectedShopperBucksAmount = 0;
        return;
      }

      // Generate options in $5 increments up to available balance or $15 max
      const maxAmount = Math.min(this.shopperBucks, 15);
      for (let amount = 5; amount <= maxAmount; amount += 5) {
        this.shopperBucksOptions.push(amount);
      }

      // If they have an amount that's not a multiple of 5, add the full amount
      if (this.shopperBucks > 0 && this.shopperBucks % 5 !== 0 && this.shopperBucks <= 15) {
        this.shopperBucksOptions.push(parseFloat(this.shopperBucks.toFixed(2)));
        this.shopperBucksOptions.sort((a, b) => a - b);
      }

      // Set default selection to the first option if available
      if (this.shopperBucksOptions.length > 0) {
        this.selectedShopperBucksAmount = this.shopperBucksOptions[0];
      } else {
        this.selectedShopperBucksAmount = 0;
      }
    },
    onShopperBucksToggle() {
      if (!this.applyShopperBucks) {
        this.selectedShopperBucksAmount = 0;
      } else if (this.shopperBucksOptions.length > 0) {
        this.selectedShopperBucksAmount = this.shopperBucksOptions[0];
      }
    },
    calculateDiscountedPrice(originalPrice, discount) {
      try {
        const price = parseFloat(originalPrice) || 0;
        const discountPercent = parseFloat(discount) || 0;
        const discountedPrice = price * (1 - discountPercent / 100);
        return Number.isFinite(discountedPrice) && discountedPrice >= 0 ? discountedPrice : 0;
      } catch (error) {
        console.error('Error calculating discounted price:', error);
        return 0;
      }
    },
    async saveBillingDetails() {
      // Debounce the save to localStorage
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
      }
      this.messageTimeout = setTimeout(() => {
        const details = { ...this.billingDetails };
        // Remove empty fields
        Object.keys(details).forEach(key => {
          if (details[key] === '' || details[key] === null) {
            delete details[key];
          }
        });
        localStorage.setItem('billingDetails', JSON.stringify(details));
      }, 300);
    },
    async loadBillingDetails() {
      // Load billing details from localStorage
      try {
        const storedDetails = localStorage.getItem('billingDetails');
        if (storedDetails) {
          this.billingDetails = JSON.parse(storedDetails);
        }
      } catch (error) {
        console.error('Error loading billing details:', error);
      }
    },
    updateCartItems(event) {
      try {
        if (event && event.detail) {
          this.checkoutCartItems = Array.isArray(event.detail) ? event.detail : [];
        } else {
          const storedItems = localStorage.getItem('checkoutCartItems');
          this.checkoutCartItems = storedItems ? JSON.parse(storedItems) : [];
        }
      } catch (error) {
        console.error('Error updating cart items:', error);
        this.checkoutCartItems = [];
      }
    },
    async applyCoupon() {
      if (!this.couponCode) {
        this.couponMessage = 'Please enter a valid coupon code.';
        return;
      }
      this.isProcessing = true;
      try {
        // Map cart items to include the already-discounted price
        const cartItemsWithDiscountedPrice = this.checkoutCartItems.map(item => ({
          ...item,
          totalCost: this.calculateDiscountedPrice(item.originalPrice, item.discount)
        }));

        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/discounts/apply`, {
          code: this.couponCode,
          subtotal: parseFloat(this.subtotalCost) || 0,
          cartItems: cartItemsWithDiscountedPrice // Send cart items with discounted prices
        });
        if (response.data.valid) {
          // Check if there's a minimum spend requirement
          if (response.data.minimumSpend && parseFloat(this.subtotalCost) < parseFloat(response.data.minimumSpend)) {
            this.couponMessage = `This discount requires a minimum spend of $${parseFloat(response.data.minimumSpend).toFixed(2)}.`;
            this.discountAmount = 0;
            this.appliedDiscountCode = '';
            this.discountType = '';
            this.eventName = '';
          } else {
            this.discountAmount = parseFloat(response.data.discountAmount) || 0;
            this.couponMessage = response.data.message || 'Coupon applied successfully!';
            this.appliedDiscountCode = response.data.discountCode || '';
            this.discountType = response.data.discountType || '';
            this.eventName = response.data.eventName || '';
          }
        } else {
          this.couponMessage = response.data.message || 'Invalid coupon code.';
          this.discountAmount = 0;
          this.appliedDiscountCode = '';
          this.discountType = '';
          this.eventName = '';
        }
        
        // After applying the coupon, update payment intent if terms are agreed
        if (this.termsAgreed && this.clientSecret && this.clientSecret !== 'free_order') {
          const currentTotal = parseFloat(this.totalCost);
          console.log(`Coupon applied, total is now $${currentTotal}, updating payment intent`);
          this.updatePaymentIntent(currentTotal);
        }
      } catch (error) {
        console.error('Error applying coupon:', error);
        this.couponMessage = 'Error applying coupon code.';
        this.discountAmount = 0;
        this.appliedDiscountCode = '';
        this.discountType = '';
        this.eventName = '';
      } finally {
        this.isProcessing = false;
      }
    },
    async handlePayment() {
      if (!this.isFormValid) {
        this.transactionMessage = 'Please fill in all required billing details.';
        return;
      }
      if (!this.termsAgreed) {
        this.transactionMessage = 'Please agree to the terms and conditions before proceeding.';
        return;
      }
      
      const totalAmount = parseFloat(this.totalCost);
      
      // Handle free orders (covered by Shopper Bucks)
      if (totalAmount <= 0) {
        this.isProcessing = true;
        
        try {
          // Process free order directly without Stripe
          const response = await axios.post(
            `${process.env.VUE_APP_API_URL}/api/payment/confirm-order`,
            {
              userId: JSON.parse(localStorage.getItem('user'))?.id,
              items: this.checkoutCartItems,
              totalCost: 0,
              email: this.billingDetails.email,
              billingDetails: this.billingDetails,
              discountCode: this.appliedDiscountCode || null,
              discountAmount: this.discountAmount || 0,
              discountType: this.discountType || null,
              eventName: this.eventName || null,
              shopperBucksApplied: this.applyShopperBucks ? this.selectedShopperBucksAmount : 0,
              paymentMethod: 'shopper_bucks' // Indicate this was paid with Shopper Bucks
            }
          );

          if (!response.data.order) {
            throw new Error('Order confirmation failed');
          }

          // Handle successful free order
          this.transactionMessage = 'Order completed successfully! Redirecting to order confirmation...';
          
          // Redirect to order confirmation page
          await this.$router.push({
            name: 'OrderConfirmation',
            params: {
              userId: response.data.order.userId,
              orderId: response.data.order.orderId,
              orderData: JSON.stringify(response.data.order)
            }
          });

          // Clear checkout cart
          this.checkoutCartItems.forEach((item) => {
            this.removeFromCheckoutCart(item.giftCardId, item.name);
          });
          
        } catch (error) {
          console.error('Free order processing error:', error);
          this.transactionMessage = error.message || 'An error occurred during order processing.';
        } finally {
          this.isProcessing = false;
        }
        return;
      }
      
      // Regular payment processing for orders with amount > $0
      if (!this.stripe || !this.clientSecret || this.clientSecret === 'free_order') {
        this.transactionMessage = 'Payment system is not fully loaded. Please wait or refresh the page.';
        return;
      }
      
      this.isProcessing = true;
      this.paymentIntentProcessing = true;
      
      try {
        // With Payment Element, we need to confirm the payment directly
        // rather than creating a payment method first
        const { error: submitError } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            // Return URL after payment is complete
            return_url: `${process.env.VUE_APP_FRONTEND_URL || window.location.origin}/order-confirmation`,
            payment_method_data: {
              billing_details: {
                name: `${this.billingDetails.firstName} ${this.billingDetails.lastName}`,
                email: this.billingDetails.email,
                phone: this.billingDetails.phone,
                address: {
                  line1: this.billingDetails.address,
                  city: this.billingDetails.city,
                  postal_code: this.billingDetails.zipCode,
                  country: this.billingDetails.country,
                },
              },
            },
          },
          redirect: 'if_required',
        });

        if (submitError) {
          throw new Error(submitError.message);
        }

        // Process regular paid order
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/payment/confirm-order`,
          {
            userId: JSON.parse(localStorage.getItem('user'))?.id,
            items: this.checkoutCartItems,
            totalCost: parseFloat(this.totalCost),
            email: this.billingDetails.email,
            billingDetails: this.billingDetails,
            discountCode: this.appliedDiscountCode || null,
            discountAmount: this.discountAmount || 0,
            discountType: this.discountType || null,
            eventName: this.eventName || null,
            shopperBucksApplied: this.applyShopperBucks ? this.selectedShopperBucksAmount : 0
          }
        );

        if (!response.data.order) {
          throw new Error('Order confirmation failed');
        }

        this.transactionMessage = 'Payment successful! Redirecting to order confirmation...';
        
        await this.$router.push({
          name: 'OrderConfirmation',
          params: {
            userId: response.data.order.userId,
            orderId: response.data.order.orderId,
            orderData: JSON.stringify(response.data.order)
          }
        });

        this.checkoutCartItems.forEach((item) => {
          this.removeFromCheckoutCart(item.giftCardId, item.name);
        });
        
      } catch (error) {
        console.error('Payment error:', error);
        this.transactionMessage = error.message || 'An error occurred during payment processing.';
      } finally {
        this.isProcessing = false;
        this.paymentIntentProcessing = false;
      }
    },
    formatExpiryDate(event) {
      let input = event.target.value.replace(/\D/g, '').substring(0, 4);
      if (input.length > 2) {
        input = `${input.substring(0, 2)}/${input.substring(2, 4)}`;
      }
      this.cardDetails.expiryDate = input;
    },
    validateCVV(event) {
      let input = event.target.value.replace(/\D/g, '').substring(0, 5);
      this.cardDetails.cvv = input;
    },
    validateCardNumber(event) {
      let input = event.target.value.replace(/\D/g, '').substring(0, 17);
      this.cardDetails.cardNumber = input;
    },
    validateCard() {
      const cardNumber = this.cardDetails.cardNumber.replace(/\s/g, '');
      if (!/^\d{15,16}$/.test(cardNumber)) {
        throw new Error('Invalid card number');
      }
      const [month, year] = this.cardDetails.expiryDate.split('/');
      if (!month || !year || month < 1 || month > 12) {
        throw new Error('Invalid expiry date');
      }
      if (!/^\d{3,4}$/.test(this.cardDetails.cvv)) {
        throw new Error('Invalid CVV');
      }
    },
    viewFinePrint(item) {
      if (item.giftCardId) {
        const navigationPromise = this.$router.push(`/gift-card/${item.giftCardId}`);
        navigationPromise.then(() => {
          setTimeout(() => {
            const finePrintElement = document.getElementById('fine-print');
            if (finePrintElement) {
              finePrintElement.scrollIntoView({ behavior: 'smooth' });
            } else {
              const finePrintSection = document.querySelector('.fine-print-section');
              if (finePrintSection) {
                finePrintSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                console.warn('Fine print section not found on gift card page');
              }
            }
          }, 1000);
        }).catch(error => {
          console.error('Navigation error:', error);
        });
      } else {
        console.error('No gift card ID found for this item');
      }
    },
  },
};
</script>

<style scoped>
.checkout-page {
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Arial', sans-serif;
  margin-bottom: 50px;
}

.checkout-container {
  display: flex;
  justify-content: space-between;
  gap: 30px;
}

.billing-details, .order-summary {
  width: 48%;
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.billing-details h2, .order-summary h2 {
  font-size: 1.5em;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input, .form-group select {
  width: 95%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row .form-group {
  flex: 1;
  margin-right: 10px;
}

.form-row .form-group:last-child {
  margin-right: 0;
}

.place-order-button {
  width: 100%;
  padding: 15px;
  background-color: #4C6B30;
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 20px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.place-order-button:hover {
  background-color: #3d5626;
}

.order-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
}

.item-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.fine-print-link {
  background: none;
  border: none;
  color: #2196F3;
  text-decoration: underline;
  padding: 0;
  font-size: 12px;
  cursor: pointer;
  margin-top: 5px;
  text-align: left;
}

.fine-print-link:hover {
  color: #0b7dda;
}

.subtotal-cost, .tax-cost, .total-cost {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  padding: 10px 0;
}

.total-cost {
  border-top: 2px solid #333;
  font-size: 1.2em;
  margin-top: 20px;
}

#card-element {
  border: 1px solid #ccc;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.transaction-message {
  margin-top: 30px;
  color: green;
  font-size: 1.2em;
  font-weight: bold;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
}

.transaction-message.fade-out {
  opacity: 0;
}

.payment-form {
  margin-top: 20px;
  position: relative;
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.payment-form .form-group {
  margin-bottom: 20px;
}

.payment-form .form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.payment-form .form-group input {
  width: 95%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.payment-form .form-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.payment-form .form-row .form-group {
  flex: 1;
  margin-right: 10px;
}

.payment-form .form-row .form-group:last-child {
  margin-right: 0;
}

.payment-form .place-order-button {
  width: 100%;
  padding: 15px;
  background-color: #4C6B30;
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 20px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.payment-form .place-order-button:hover {
  background-color: #3d5626;
}

.processing-spinner {
  display: flex;
  align-items: center;
  margin-top: 20px;
}

.processing-spinner .spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #4C6B30;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

.coupon-code {
  margin: 20px 0;
  padding: 20px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.coupon-code:focus-within {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.coupon-code label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.coupon-code input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e1e7ef;
  border-radius: 6px;
  font-size: 0.95em;
  transition: border-color 0.2s ease;
}

.coupon-code input:focus {
  outline: none;
  border-color: #4C6B30;
}

.coupon-code button {
  padding: 12px 24px;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  margin-left: 10px;
}

.coupon-code button:hover:not(:disabled) {
  background-color: #3d5626;
  transform: translateY(-1px);
}

.coupon-code button:disabled {
  background-color: #a8b8a8;
  cursor: not-allowed;
}

.coupon-message {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.coupon-message.success {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.coupon-message.error {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.coupon-message::before {
  content: '';
  width: 16px;
  height: 16px;
  background-size: contain;
  background-repeat: no-repeat;
}

.coupon-message.success::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23065f46'%3E%3Cpath fill-rule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clip-rule='evenodd'/%3E%3C/svg%3E");
}

.coupon-message.error::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23991b1b'%3E%3Cpath fill-rule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clip-rule='evenodd'/%3E%3C/svg%3E");
}

.shopper-bucks-section {
  margin: 20px 0;
  padding: 20px;
  background: #f0f7ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.shopper-bucks-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.shopper-bucks-header h3 {
  margin: 0;
  color: #2c5282;
  font-size: 1.1em;
}

.available-amount {
  color: #2c5282;
  font-weight: 600;
  font-size: 0.9em;
}

.shopper-bucks-controls {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkbox-container input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.checkbox-container label {
  margin: 0;
  font-weight: 500;
  color: #2c5282;
  cursor: pointer;
}

.amount-selection {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 25px;
  animation: fadeIn 0.3s ease;
}

.amount-selection label {
  font-size: 0.9em;
  color: #2c5282;
  font-weight: 500;
}

.amount-dropdown {
  width: 150px;
  padding: 8px 12px;
  border: 2px solid #b3d9ff;
  border-radius: 6px;
  font-size: 0.95em;
  background-color: white;
  color: #2c5282;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.amount-dropdown:focus {
  outline: none;
  border-color: #4C6B30;
}

.shopper-bucks-discount {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  color: #2c5282;
  font-size: 16px;
  font-weight: 600;
  border-top: 1px dashed #e5e7eb;
  margin-top: 10px;
}

.shopper-bucks-discount span:first-child::before {
  content: '💰';
  margin-right: 8px;
}

.discount-amount {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  color: #059669;
  font-size: 16px;
  font-weight: 600;
  border-top: 1px dashed #e5e7eb;
  margin-top: 10px;
}

.discount-amount span:first-child::before {
  content: '🏷️';
  margin-right: 8px;
}

.terms-checkbox {
  margin: 20px 0;
  font-size: 14px;
}

.terms-checkbox a {
  color: #4C6B30;
  text-decoration: none;
}

.terms-checkbox a:hover {
  text-decoration: underline;
}

.terms-agreement {
  display: flex;
  align-items: flex-start;
  margin: 20px 0;
  padding: 15px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  text-align: left;
}

.terms-agreement input {
  margin-right: 10px;
  margin-top: 3px;
}

.terms-agreement label {
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.terms-agreement a {
  color: #4C6B30;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.terms-agreement a:hover {
  color: #6E9F49;
}

.payment-section {
  margin-top: 25px;
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
  .checkout-container {
    flex-direction: column;
    align-items: center;
  }
  .order-summary, .billing-details {
    width: 100%;
    max-width: 600px;
  }
  .form-row {
    flex-direction: column;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .checkout-page {
    max-width: 100%;
    padding: 0 20px;
  }
  .checkout-container {
    padding: 0;
    flex-direction: column;
    align-items: center;
  }
  .form-group input, .form-group select, #card-element {
    padding: 8px;
  }
  .place-order-button {
    padding: 10px;
    font-size: 16px;
  }
  .coupon-code button {
    width: 100%;
  }
  .amount-selection {
    padding-left: 0;
  }
  .shopper-bucks-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  .amount-dropdown {
    width: 100%;
  }
  .order-summary, .billing-details {
    width: 100%;
    max-width: 600px;
  }
  .form-row {
    flex-direction: column;
    gap: 20px;
  }
  .order-item {
    flex-direction: column;
  }
  .item-price {
    margin-top: 10px;
    align-self: flex-end;
  }
}

#payment-element {
  margin-bottom: 24px;
  padding-top: 12px;
}

.hidden {
  display: none;
}

#submit {
  margin-top: 24px;
  position: relative;
}

#spinner {
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

.event-ticket-item {
  margin-bottom: 15px;
  padding: 15px;
  border-radius: 8px;
  background-color: #f0f7ff;
  border-left: 4px solid #4C6B30;
}

.event-ticket-item .item-name {
  font-weight: bold;
  margin-bottom: 5px;
  color: #2c3e50;
}

.shopper-bucks {
  margin: 20px 0;
  padding: 20px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.shopper-bucks label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.shopper-bucks-amount {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.shopper-bucks-amount select {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e1e7ef;
  border-radius: 6px;
  font-size: 0.95em;
  transition: border-color 0.2s ease;
}

.shopper-bucks-amount select:focus {
  outline: none;
  border-color: #4C6B30;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #4C6B30;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Shopper Bucks Section */
.shopper-bucks-section {
  margin: 20px 0;
  padding: 20px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.shopper-bucks-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.shopper-bucks-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1em;
}

.available-amount {
  color: #4C6B30;
  font-weight: 600;
  font-size: 0.95em;
}

.shopper-bucks-controls {
  margin-top: 15px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.checkbox-container input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #4C6B30;
}

.checkbox-container label {
  color: #2c3e50;
  font-weight: 500;
  cursor: pointer;
}

.amount-selection {
  margin-top: 15px;
}

.amount-selection label {
  display: block;
  margin-bottom: 8px;
  color: #2c3e50;
  font-weight: 500;
}

.amount-dropdown {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e7ef;
  border-radius: 6px;
  font-size: 0.95em;
  background: white;
  transition: border-color 0.2s ease;
}

.amount-dropdown:focus {
  outline: none;
  border-color: #4C6B30;
}

/* Shopper Bucks Unavailable Message */
.shopper-bucks-unavailable {
  margin: 20px 0;
  padding: 20px;
  background: #faf9f7;
  border: 1px solid #f0ede6;
  border-radius: 8px;
}

.unavailable-message {
  color: #8b7355;
  font-style: italic;
  margin-top: 10px;
  padding: 10px;
  background: #f7f4ed;
  border-radius: 4px;
  border-left: 3px solid #d4af37;
}

/* Free Order Section */
.free-order-section {
  margin: 20px 0;
  padding: 30px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #4C6B30;
  border-radius: 12px;
  text-align: center;
}

.free-order-section h2 {
  color: #4C6B30;
  margin-bottom: 20px;
  font-size: 1.5em;
}

.free-order-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: #4C6B30;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  font-weight: bold;
}

.free-order-message p {
  color: #2c3e50;
  font-size: 1.1em;
  margin: 0;
  max-width: 400px;
}

.complete-order-button {
  background: #4C6B30;
  color: white;
  border: none;
  padding: 15px 40px;
  border-radius: 8px;
  font-size: 1.1em;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 50px;
}

.complete-order-button:hover:not(:disabled) {
  background: #3a5426;
}

.complete-order-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>